import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  useGetPublicAgainstShopQuery,
  useGetAllConnectedQuery,
  useGetRandomSuggestionsQuery,
  useSendConnectionRequestMutation,
  useDisconnectMutation,
} from "@/features/connection/connectionApiSlice";
import { SuccessNotify, FailedNotify } from "@/constants/tostify";
import { useGetApprovedMarketersQuery } from "@/features/marketing/marketingApiSlice";

const normBT = (bt) =>
  String(bt || "")
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

const isMarketerBT = (bt) => {
  const n = normBT(bt);
  return (
    n === "marketerprofile" ||
    n === "marketer" ||
    n === "marketershop" ||
    n === "approvedmarketers"
  );
};

const idStr = (v) => {
  if (v == null || v === "" || v === "null" || v === "undefined") return null;
  if (typeof v === "object") return String(v._id || v.id || v) || null;
  const s = String(v);
  return s && s !== "null" && s !== "undefined" && s !== "[object Object]"
    ? s
    : null;
};

export const useViewFriendsState = ({ id }) => {
  const { name } = useSelector((state) => state.user);
  const location = useLocation();

  const selectedBT = location.state?.ViewConnectedProfiles;
  // True when the user clicked "View all" from /marketing-profile page.
  // This flag tells the getAllConnected query to apply marketer-profile
  // filtering so the same connections shown on that page appear here.
  const fromMarketerProfile = location.state?.fromMarketerProfile === true;

  // UI state
  const [key, setKey] = useState("home");
  const [activeTab, setActiveTab] = useState(selectedBT || "");
  const [searchText, setSearchText] = useState("");
  const [sendingConnect, setSendingConnect] = useState({});
  const [page, setPage] = useState(1);
  const [randomPage, setRandomPage] = useState(1);

  useEffect(() => {
    if (selectedBT) {
      setActiveTab(selectedBT);
    }
  }, [selectedBT]);

  useEffect(() => {
    setPage(1);
    setRandomPage(1);
  }, [selectedBT, searchText]);

  const [createPost, setCreatePost] = useState({
    shopid: "",
    userid: name?._id,
    businessType: "generalPublic",
    my: [
      {
        heading: "",
        content: "",
        img: [""],
        video: [""],
      },
    ],
  });

  // Derived query parameters
  const businessTypeParams = {
    businessType: "generalPublic",
    userid: name?._id,
  };

  const allConnectedParams = {
    userid: name?._id,
    businessType: selectedBT,
    page,
    limit: 30,
    // Apply marketer-profile server-side filter when coming from /marketing-profile
    ...(fromMarketerProfile ? { isMarketerProfile: true } : {}),
  };

  // Queries
  const { data: publicAgainstShopData, isLoading: isPublicLoading } =
    useGetPublicAgainstShopQuery(businessTypeParams, { skip: !name?._id });

  const {
    data: connectedDataResponse,
    isLoading: isConnectedLoading,
    isFetching: isConnectedFetching,
  } = useGetAllConnectedQuery(allConnectedParams, { skip: !name?._id });

  const { data: approvedMarketersData } = useGetApprovedMarketersQuery(
    // Pass sameZone:true so the server filters by the user's zone — this
    // returns all approved marketers in the same zone, not just connected ones.
    name?._id ? { userId: name._id, sameZone: true } : undefined,
    {
      // Skip when marketing "View all" — show connected marketers, not zone list
      skip:
        selectedBT !== "marketerProfile" ||
        !name?._id ||
        fromMarketerProfile,
    },
  );
  const approvedMarketers = approvedMarketersData?.data ?? [];

  /** Map a flat connection row to the "other party" tile for the current user */
  const mapConnectedRowToItem = (c) => {
    const me = idStr(name?._id);
    const iAmSender = idStr(c?.senderid) === me;
    const otherUser = iAmSender ? c?.receiverUser : c?.senderUser;
    const userid = iAmSender ? c?.recieverid : c?.senderid;
    const shopid = iAmSender
      ? c?.recievershopid || c?.recieverShopContextId
      : c?.sendershopid || c?.senderShopContextId;
    const shopname =
      (iAmSender
        ? c?.recievershopname ||
          c?.recieverShopContextName ||
          otherUser?.fullname
        : c?.sendershopname ||
          c?.senderShopContextName ||
          otherUser?.fullname) ||
      otherUser?.fullname ||
      "";
    const image =
      (iAmSender
        ? c?.recievershopimage ||
          c?.recieverShopContextImage ||
          otherUser?.imageUrl ||
          otherUser?.image
        : c?.sendershopimage ||
          c?.senderShopContextImage ||
          otherUser?.imageUrl ||
          otherUser?.image) ||
      otherUser?.imageUrl ||
      "";
    const otherBT = iAmSender ? c?.businessType : c?.senderBusinessType;
    return {
      _id: idStr(shopid) || idStr(userid) || c?._id,
      userid,
      shopid: idStr(shopid) || null,
      shopname: shopname || otherUser?.fullname || "User",
      fullname: otherUser?.fullname || shopname || "",
      image,
      imageUrl: image,
      businessType: otherBT || selectedBT,
      linkBusinessType: otherBT || selectedBT,
      asMarketerProfile: c?.asMarketerProfile === true,
      senderUser: c?.senderUser,
      receiverUser: c?.receiverUser,
    };
  };

  const rowMatchesSelectedBT = (c) => {
    if (!selectedBT) return true;
    const me = idStr(name?._id);
    const iAmSender = idStr(c?.senderid) === me;
    const otherBT = iAmSender ? c?.businessType : c?.senderBusinessType;
    const nSel = normBT(selectedBT);
    const nOther = normBT(otherBT);
    const nRecv = normBT(c?.businessType);
    const nSend = normBT(c?.senderBusinessType);
    if (isMarketerBT(selectedBT)) {
      return (
        isMarketerBT(otherBT) ||
        isMarketerBT(c?.businessType) ||
        isMarketerBT(c?.senderBusinessType)
      );
    }
    return nOther === nSel || nRecv === nSel || nSend === nSel;
  };

  const connectedData = useMemo(() => {
    const connectedBT = connectedDataResponse?.connectedBT || [];

    // Primary: matching businessType in connectedBT (normalize aliases)
    const matchFromBT = connectedBT.find(
      (bt) =>
        normBT(bt?.businessType) === normBT(selectedBT) ||
        (isMarketerBT(selectedBT) && isMarketerBT(bt?.businessType)),
    );

    // From marketing "View all": always prefer real connected tiles for that BT
    // (including marketerProfile). Do NOT replace with zone-wide approved list.
    if (matchFromBT) {
      const items = Array.isArray(matchFromBT.items)
        ? matchFromBT.items.filter(
            (it) =>
              it &&
              !it.isPlaceholder &&
              (it.userid || it.shopid || it._id || it._marketerDocId),
          )
        : [];
      if (items.length > 0 || fromMarketerProfile) {
        return {
          ...matchFromBT,
          businessType: matchFromBT.businessType || selectedBT,
          count: items.length || matchFromBT.count || 0,
          items,
        };
      }
    }

    // Fallback: build tiles from flat `connected` rows (hydrated users/shops)
    if (selectedBT && Array.isArray(connectedDataResponse?.connected)) {
      const matchingConnected = (connectedDataResponse.connected || []).filter(
        rowMatchesSelectedBT,
      );
      if (matchingConnected.length > 0) {
        const items = matchingConnected.map(mapConnectedRowToItem);
        // Dedupe by userid|shopid
        const seen = new Set();
        const unique = [];
        for (const it of items) {
          const key = `${idStr(it.userid) || ""}|${idStr(it.shopid) || "null"}`;
          if (seen.has(key)) continue;
          seen.add(key);
          unique.push(it);
        }
        return {
          businessType: selectedBT,
          count: unique.length,
          items: unique,
        };
      }
    }

    // Legacy browse: personal marketerProfile "Approved Marketers" (same-zone list)
    // when there are no connected marketer tiles yet.
    if (selectedBT === "marketerProfile" && !fromMarketerProfile) {
      return {
        businessType: "Approved Marketers",
        count: approvedMarketers.length,
        items: approvedMarketers.map((m) => ({
          _id: m._id,
          // Use marketerId (MA-xxx) as the view ID — the view-marketer-profile
          // route decodes this via encodeId/decodeId and validates it.
          userid: m.marketerId || m?.userId?._id || m?.userId,
          marketerId: m.marketerId,
          fullname: m?.userId?.fullname || "Anonymous",
          shopname: m?.userId?.fullname || "Anonymous",
          imageUrl: m?.userId?.imageUrl,
          image: m?.userId?.imageUrl,
          businessType: "marketerProfile",
        })),
      };
    }

    return matchFromBT || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- row match uses selectedBT/name
  }, [
    selectedBT,
    connectedDataResponse,
    approvedMarketers,
    fromMarketerProfile,
    name?._id,
  ]);

  const randomProfilesParams = {
    userid: name?._id,
    businessType: "generalPublic",
    FindBT: connectedData?.businessType || selectedBT,
    page: randomPage,
    limit: 30,
  };

  const {
    data: randomProfilesData,
    isLoading: isRandomLoading,
  } = useGetRandomSuggestionsQuery(randomProfilesParams, {
    skip: !name?._id || !(connectedData?.businessType || selectedBT),
  });

  // Mutations
  const [disconnectConnection] = useDisconnectMutation();
  const [sendConnectionRequest] = useSendConnectionRequestMutation();

  // Processing dynamic tabs data (memoised to keep reference stable)
  const tabsData = useMemo(() => {
    if (!publicAgainstShopData?.data) return [];
    const responseData = publicAgainstShopData.data;
    return Object.keys(responseData).map((k) => ({
      eventKey: k,
      title: k.charAt(0).toUpperCase() + k.slice(1).replace(/([A-Z])/g, " $1"),
      data: responseData[k],
    }));
  }, [publicAgainstShopData]);

  // Set active tab initially
  useEffect(() => {
    if (tabsData.length > 0) {
      const isValidTab =
        tabsData.some((t) => t.eventKey === activeTab) ||
        activeTab === "marketerProfile" ||
        activeTab === "home";
      if (!isValidTab) {
        const firstKey = tabsData[0].eventKey;
        setActiveTab(firstKey);
      }
    }
  }, [tabsData, activeTab]);

  const handleTabSelect = (eventKey) => {
    setActiveTab(eventKey);
  };

  const getActiveTabData = () => {
    const tabData = tabsData.find((tab) => tab.eventKey === activeTab);
    return tabData ? tabData.data : [];
  };

  const handleRemove = async (item) => {
    const itemBT =
      item?.linkBusinessType ||
      item?.businessType ||
      connectedData?.businessType ||
      selectedBT;
    const isGP =
      itemBT === "generalPublic" ||
      item?.generalPublic === true ||
      normBT(itemBT) === "generalpublic";
    const isMarketerTarget = isMarketerBT(itemBT);
    const targetUserId =
      item?.userid?._id || item?.userid || item?._id || null;
    // Shop connects keep shop id; GP / marketer person tiles do not
    const targetShopId =
      isGP || isMarketerTarget
        ? undefined
        : item?.shopid?._id || item?.shopid || undefined;

    if (!targetUserId) {
      FailedNotify("Cannot remove: missing profile id");
      return;
    }

    try {
      const payload = {
        senderid: name?._id,
        // Marketing profile connects are person-as-marketer (no sender shop)
        sendershopid:
          fromMarketerProfile || isGP || isMarketerTarget
            ? undefined
            : id || undefined,
        senderBusinessType: fromMarketerProfile
          ? "marketerProfile"
          : "generalPublic",
        businessType: itemBT || "generalPublic",
        recieverid: targetUserId,
        ...(targetShopId ? { recievershopid: targetShopId } : {}),
        ...(fromMarketerProfile || item?.asMarketerProfile === true
          ? { asMarketerProfile: true }
          : {}),
      };

      await disconnectConnection(payload).unwrap();
      SuccessNotify("Connection removed successfully");
    } catch (error) {
      console.error("Remove connection failed:", error);
      FailedNotify(error?.data?.message || "Failed to remove connection");
    }
  };

  const handleConnect = async (e) => {
    const isGP =
      e?.businessType === "generalPublic" || e?.generalPublic === true;
    const targetUserId = e?.userid || e?._id;
    const targetShopId = isGP ? undefined : e?.shopid || e?._id;
    const objectKey = e?._id || e?.userid || e?.shopid;
    const targetBT = isGP
      ? "generalPublic"
      : e?.businessType || connectedData?.businessType || selectedBT;

    try {
      setSendingConnect((prev) => ({ ...prev, [objectKey]: true }));

      const payload = {
        senderid: name?._id,
        senderBusinessType: fromMarketerProfile
          ? "marketerProfile"
          : "generalPublic",
        // Don't attach a shop ID for general public connections — the server
        // uses sendershopid to apply same-zone filtering, which must not
        // apply to generalPublic connections.
        sendershopid: isGP || fromMarketerProfile ? undefined : id,
        businessType: targetBT,
        recieverid: targetUserId,
        recievershopid: targetShopId,
        ...(fromMarketerProfile ? { asMarketerProfile: true } : {}),
      };

      await sendConnectionRequest(payload).unwrap();
      SuccessNotify("Connect request sent successfully!");
      setSendingConnect((prev) => ({ ...prev, [objectKey]: false }));
    } catch (error) {
      console.error("Send connect request failed:", error);
      FailedNotify(
        error?.data?.message || "Something went wrong while sending request",
      );
      setSendingConnect((prev) => ({ ...prev, [objectKey]: false }));
    }
  };

  const getConnectionStatus = (e) => {
    const isGP =
      e?.businessType === "generalPublic" || e?.generalPublic === true;
    const targetUserId = e?.userid || e?._id;
    const targetShopId = isGP ? undefined : e?.shopid || e?._id;
    const targetBT = e?.businessType || selectedBT;

    const isMatch = (c, role) => {
      if (role === "receiver") {
        const matchesId = targetShopId
          ? String(c.recievershopid) === String(targetShopId)
          : String(c.recieverid) === String(targetUserId) && !c.recievershopid;
        return matchesId && (!targetBT || c.businessType === targetBT);
      } else {
        const matchesId = targetShopId
          ? String(c.sendershopid) === String(targetShopId)
          : String(c.senderid) === String(targetUserId) && !c.sendershopid;
        return matchesId && (!targetBT || c.senderBusinessType === targetBT);
      }
    };

    if (
      connectedDataResponse?.connected?.some(
        (c) => isMatch(c, "receiver") || isMatch(c, "sender"),
      )
    ) {
      return "Connected";
    }

    if (connectedDataResponse?.requested?.some((c) => isMatch(c, "receiver"))) {
      return "Pending";
    }

    if (connectedDataResponse?.pending?.some((c) => isMatch(c, "sender"))) {
      return "Accept";
    }

    return "Connect";
  };

  const randomProfiles = randomProfilesData?.data || [];

  const getItemSearchName = (item) =>
    item?.shopname ||
    item?.fullname ||
    item?.companyname ||
    item?.profileName ||
    "";

  const filteredConnected =
    connectedData?.items?.filter((item) =>
      getItemSearchName(item)
        .toLowerCase()
        .includes(searchText.toLowerCase()),
    ) || [];

  // Build a Set of connected user/shop IDs for O(1) lookup
  const connectedIds = new Set();
  (connectedDataResponse?.connected || []).forEach((c) => {
    if (!selectedBT || rowMatchesSelectedBT(c)) {
      if (c?.recieverid) connectedIds.add(c.recieverid);
      if (c?.senderid) connectedIds.add(c.senderid);
      if (c?.recievershopid) connectedIds.add(c.recievershopid);
      if (c?.sendershopid) connectedIds.add(c.sendershopid);
    }
  });

  const getName = (item) =>
    item?.shopname ||
    item?.fullname ||
    item?.companyname ||
    item?.profileName ||
    "";

  const rawFilteredRandom = randomProfiles.filter((item) => {
    // Hide already-connected/pending/accept users in real-time
    const status = getConnectionStatus(item);
    if (status !== "Connect") return false;

    return getName(item).toLowerCase().includes(searchText.toLowerCase());
  });

  // Remove duplicates by _id (fallback: by name) so items with empty names
  // don't all collapse to a single entry.
  const filteredRandom = rawFilteredRandom.filter((item, index, self) => {
    const idKey = item?._id || item?.userid || item?.shopid;
    if (idKey) {
      return (
        index ===
        self.findIndex((t) => (t?._id || t?.userid || t?.shopid) === idKey)
      );
    }
    // No ID available — fall back to name dedup
    return index === self.findIndex((t) => getName(t) === getName(item));
  });

  return {
    key,
    setKey,
    searchText,
    setSearchText,
    createPost,
    setCreatePost,
    loading: !name?._id || isPublicLoading,
    isConnectedLoading:
      !name?._id || isConnectedLoading || isConnectedFetching,
    isRandomLoading: !name?._id || isRandomLoading,
    tabsData,
    activeTab,
    handleTabSelect,
    getActiveTabData,
    connectedData,
    filteredConnected,
    filteredRandom,
    sendingConnect,
    handleRemove,
    handleConnect,
    getConnectionStatus,
    selectedBT,
    fromMarketerProfile,
    page,
    setPage,
    total: connectedData?.count || connectedDataResponse?.total || 0,
    randomPage,
    setRandomPage,
    randomTotal: randomProfilesData?.total || 0,
  };
};
